import { Virtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useState } from "react";
import {
  CollapsibleState,
  isCollapsibleExpanded,
} from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";

function isNodeExpanded(node: FlatTreeNode): boolean {
  return isCollapsibleExpanded(node.collapsibleState);
}

interface Options {
  scrollRef: React.RefObject<HTMLElement>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  nodes: FlatTreeNode[];
  activeStickyIndexRef: React.MutableRefObject<number[]>;
  stickyElsRef: React.MutableRefObject<Map<number, HTMLElement>>;
  stickyNodeIndices: number[];
  startByIndex: number[];
  stickyAncestorHeightByIndex: number[];
  maxStickyHeight: number;
  offsetTop: number;
}

/**
 * Scan sticky-eligible nodes in flat order, build the active pinned stack
 * from those whose natural Y has crossed below their sticky-top. The same
 * algorithm runs in StepTree's rangeExtractor (for renders that already
 * happened due to range changes) and in the scroll handler below (to
 * trigger a re-render when scroll alone causes a threshold crossing).
 *
 * Uses precomputed `startByIndex` rather than the virtualizer's
 * measurementsCache so it's deterministic and not subject to cache lag
 * after tree changes or virtualizer-window re-entries.
 */
export function computeActiveStack(
  scrollOffset: number,
  stickyNodeIndices: number[],
  nodes: FlatTreeNode[],
  startByIndex: number[],
  stickyAncestorHeightByIndex: number[],
  maxStickyHeight: number,
): number[] {
  // Binary search for the largest k such that the row's start is small
  // enough to *potentially* be past its threshold. Any row whose start
  // exceeds scrollOffset + maxStickyHeight has naturalY > maxStickyHeight,
  // which is ≥ its own stickyTop — so it can't be past.
  let lo = 0;
  let hi = stickyNodeIndices.length - 1;
  let upper = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (startByIndex[stickyNodeIndices[mid]] <= scrollOffset + maxStickyHeight) {
      upper = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  // Walk backward from `upper` to find the deepest row actually past its
  // threshold. Terminates immediately at deep scroll (the deepest row is
  // near the top of the scanned range), and `upper` keeps the walk
  // bounded at shallow scroll. The threshold is `<=` rather than `<` so
  // a row sitting exactly at its sticky-top (the topmost sticky at
  // scroll=0, plus immediate sticky descendants) is treated as stuck
  // from the start — otherwise it'd render position:absolute on mount
  // and flip to sticky on the first scroll, producing a one-frame snap.
  //
  // Collapsed rows are skipped: their children aren't in the flat tree,
  // so there's no content scrolling beneath them — pinning the header
  // would just stack an empty-section row under its ancestors. The next
  // shallower past row takes over instead.
  let deepestPastIdx = -1;
  for (let pos = upper; pos >= 0; pos--) {
    const idx = stickyNodeIndices[pos];
    if (nodes[idx].collapsibleState === CollapsibleState.Collapsed) {
      continue;
    }
    const naturalY = startByIndex[idx] - scrollOffset;
    const stickyTop = stickyAncestorHeightByIndex[idx];
    if (naturalY <= stickyTop) {
      deepestPastIdx = idx;
      break;
    }
  }

  if (deepestPastIdx < 0) {
    return [];
  }

  // Sticky ancestors of the deepest past row are guaranteed past too: a
  // child's threshold is its parent's threshold + parent.height, and the
  // child's start is at least parent.start + parent.height — so child
  // past ⇒ parent past. Chain them straight from parentPath without
  // re-checking. The deepest past row is by definition the last past
  // row in flat order, so no later sibling needs popping.
  const stack: number[] = [];
  for (const parent of nodes[deepestPastIdx].parentPath) {
    if (parent.sticky) {
      stack.push(parent.index);
    }
  }
  stack.push(deepestPastIdx);
  return stack;
}

function stacksEqual(left: readonly number[], right: readonly number[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let idx = 0; idx < left.length; idx++) {
    if (left[idx] !== right[idx]) {
      return false;
    }
  }
  return true;
}

export function stickyAncestorHeight(node: FlatTreeNode): number {
  return node.parentPath.reduce((sum, parent) => sum + parent.height, 0);
}

/**
 * Tallest possible sticky stack across all nodes — the sum of every sticky
 * ancestor's height plus the node's own height when it is itself sticky.
 * Used by the virtualizer to reserve scroll padding so anchor scrolls
 * never land underneath the pinned headers.
 */
export function maxStickyHeight(nodes: FlatTreeNode[]): number {
  let max = 0;
  for (const node of nodes) {
    const total = stickyAncestorHeight(node) + (node.sticky ? node.height : 0);
    if (total > max) {
      max = total;
    }
  }
  return max;
}

/**
 * For each node, return the index of the first node in flat order that
 * lies *outside* its subtree — i.e. the row whose appearance signals
 * that this row's section has ended. Returns a map of
 * index → followerIndex.
 *
 * For a row with same-depth siblings under the same parent, the follower
 * is the next sibling (same as the previous nextSibling map). For a row
 * with no same-depth sibling (e.g. the only group in a state), the
 * follower is whatever comes after the row's whole subtree — typically
 * the next state header. Used as the push trigger so a sub-section's
 * pinned rows disengage as their section ends, sliding behind the
 * still-pinned ancestor.
 */
export function buildFollowerMap(nodes: FlatTreeNode[]): Map<number, number> {
  const map = new Map<number, number>();
  // Stack of (idx, depth) for rows whose follower we haven't yet found.
  // When a later row at depth <= entry.depth appears, the entry's subtree
  // has ended and the later row is its follower. Anything still on the
  // stack at the end has no follower (rest of tree).
  const openIdx: number[] = [];
  const openDepth: number[] = [];

  for (let idx = 0; idx < nodes.length; idx++) {
    const depth = nodes[idx].depth;
    while (openIdx.length > 0 && openDepth[openDepth.length - 1] >= depth) {
      map.set(openIdx.pop() as number, idx);
      openDepth.pop();
    }
    openIdx.push(idx);
    openDepth.push(depth);
  }

  return map;
}

/**
 * Compute the effective `top` for every pinned row in the current stack.
 *
 * iOS-contacts-style push: as a pinned row's follower (the first row
 * outside its subtree) approaches from below, pull the pinned row up
 * by the intrusion so the follower takes its place cleanly instead of
 * sliding underneath.
 *
 * Using the follower (rather than just the same-depth next sibling)
 * means a sub-section row whose section has ended gets pushed by the
 * next non-descendant — typically a sibling at its own depth, but when
 * there isn't one (e.g. the only group in a state), the next outer
 * section header. The row slides up behind its still-pinned ancestor,
 * disengaging by the time its section visually ends.
 *
 * Each row is anchored to its *static* sticky-top (offsetTop + sum of
 * ancestor heights) — not its parent's effective top — so a pushed
 * ancestor moves alone. Descendants get their own push triggered by
 * their own follower.
 */
export function computeStickyPushTops(
  pinnedIndices: number[],
  nodes: FlatTreeNode[],
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  followerMap: Map<number, number>,
  stickyAncestorHeightByIndex: number[],
  offsetTop: number,
  scrollOffset: number,
): Map<number, number> {
  const effectiveTopByIdx = new Map<number, number>();

  for (const idx of pinnedIndices) {
    const node = nodes[idx];
    const baseTop = offsetTop + stickyAncestorHeightByIndex[idx];

    // measurement.start is relative to the tree's inner container, which
    // itself sits `offsetTop` below the scroll container's top (after the
    // sticky spacer). Add offsetTop to convert to a viewport coordinate.
    let effectiveTop = baseTop;
    const followerIdx = followerMap.get(idx);
    if (followerIdx !== undefined) {
      const followerMeasurement = rowVirtualizer.measurementsCache[followerIdx];
      if (followerMeasurement) {
        const followerViewportY = offsetTop + followerMeasurement.start - scrollOffset;
        if (followerViewportY < baseTop + node.height) {
          effectiveTop = followerViewportY - node.height;
        }
      }
    }

    effectiveTopByIdx.set(idx, effectiveTop);
  }

  return effectiveTopByIdx;
}

/**
 * Write per-row push offsets to the DOM as a delta on the
 * `--sticky-push-offset` CSS custom property. TreeItem applies the value
 * via `transform: translateY(...)` so each scroll tick is a composited
 * transform change rather than a `top` change, which would re-trigger
 * sticky layout recalc on every row.
 *
 * Takes the precomputed effective tops from {@link computeStickyPushTops}
 * so the caller can reuse the same map for the shadow-anchor decision
 * (see useStickyHeaders below).
 */
function applyStickyPushTops(
  pinnedIndices: number[],
  effectiveTops: Map<number, number>,
  stickyEls: Map<number, HTMLElement>,
  stickyAncestorHeightByIndex: number[],
  offsetTop: number,
): void {
  for (const idx of pinnedIndices) {
    const stickyEl = stickyEls.get(idx);
    const effectiveTop = effectiveTops.get(idx);
    if (!stickyEl || effectiveTop === undefined) {
      continue;
    }
    // Negative delta = push up. Zero when nothing is pushing.
    const staticBaseTop = offsetTop + stickyAncestorHeightByIndex[idx];
    const pushOffset = effectiveTop - staticBaseTop;
    if (pushOffset === 0) {
      stickyEl.style.removeProperty("--sticky-push-offset");
    } else {
      stickyEl.style.setProperty("--sticky-push-offset", `${pushOffset}px`);
    }
  }
}

/**
 * Drive sticky-header presentation from a single scroll listener:
 *
 * 1. Maintain `activeStickyIndexRef` (the current pinned chain) and
 *    return `lastStuckIndex` — the deepest pinned row that's both
 *    expanded and currently at its base position. That's the row the
 *    consumer renders a shadow under: it's the stable boundary between
 *    pinned content and scrolling content. Mid-push rows are skipped so
 *    the shadow doesn't move with a sliding-out descendant.
 * 2. Apply per-row push offsets via the `--sticky-push-offset` CSS
 *    custom property, driven by each row's follower (the first row
 *    outside its subtree). Written directly to the DOM so scroll updates
 *    don't trigger React re-renders.
 *
 * One listener handles both because they read the same scrollOffset on
 * the same tick — splitting them risked a frame of disagreement between
 * the shadow and the push position. State only changes when the pinned
 * chain or the shadow target actually flips, so consumers don't
 * re-render on every scroll event.
 */
export function useStickyHeaders({
  scrollRef,
  rowVirtualizer,
  nodes,
  activeStickyIndexRef,
  stickyElsRef,
  stickyNodeIndices,
  startByIndex,
  stickyAncestorHeightByIndex,
  maxStickyHeight,
  offsetTop,
}: Options): number {
  const [lastStuckIndex, setLastStuckIndex] = useState(-1);
  // Bumping this triggers a re-render of the consumer (StepTree) so its
  // virtualItems.map sees the updated activeStickyIndexRef. We only bump
  // when the scan actually changes — typically only at threshold
  // crossings, which is much rarer than scroll events.
  const [, setActiveStickyVersion] = useState(0);
  const followerMap = useMemo(() => buildFollowerMap(nodes), [nodes]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const update = () => {
      const scrollOffset = el.scrollTop;

      // Run the same scan as rangeExtractor so a row crossing its
      // threshold mid-scroll (between range-extractor calls) gets pinned
      // immediately instead of flicking in on the next range change.
      const newStack = computeActiveStack(
        scrollOffset,
        stickyNodeIndices,
        nodes,
        startByIndex,
        stickyAncestorHeightByIndex,
        maxStickyHeight,
      );
      if (!stacksEqual(newStack, activeStickyIndexRef.current)) {
        activeStickyIndexRef.current = newStack;
        setActiveStickyVersion((version) => version + 1);
      }

      const effectiveTops = computeStickyPushTops(
        newStack,
        nodes,
        rowVirtualizer,
        followerMap,
        stickyAncestorHeightByIndex,
        offsetTop,
        scrollOffset,
      );

      // lastStuckIndex drives the "content scrolls beneath here" shadow.
      // Walk back from the deepest entry to find the deepest row that is
      // both *expanded* (a collapsed group has no content scrolling
      // beneath it) and *not currently being pushed* (a row mid-push is
      // sliding behind its ancestor — the stable boundary is the next
      // ancestor up that's still at its base position).
      //
      // Require the row to have scrolled at least 1px past its base
      // position before showing the shadow. computeActiveStack pins on
      // `naturalY <= stickyTop` to avoid a one-frame mount snap, but at
      // exactly stickyTop the next row sits flush against the sticky's
      // bottom edge — there's no content under it yet, so the shadow
      // would just sit on top of the next row's top border.
      let next = -1;
      for (let stackIdx = newStack.length - 1; stackIdx >= 0; stackIdx--) {
        const idx = newStack[stackIdx];
        if (!isNodeExpanded(nodes[idx])) {
          continue;
        }
        const naturalY = startByIndex[idx] - scrollOffset;
        if (naturalY >= stickyAncestorHeightByIndex[idx]) {
          continue;
        }
        const baseTop = offsetTop + stickyAncestorHeightByIndex[idx];
        const effectiveTop = effectiveTops.get(idx);
        if (effectiveTop !== undefined && effectiveTop !== baseTop) {
          continue;
        }
        next = idx;
        break;
      }
      setLastStuckIndex((prev) => (prev === next ? prev : next));

      applyStickyPushTops(
        newStack,
        effectiveTops,
        stickyElsRef.current,
        stickyAncestorHeightByIndex,
        offsetTop,
      );
    };

    el.addEventListener("scroll", update);
    update();

    return () => {
      el.removeEventListener("scroll", update);
    };
  }, [
    scrollRef,
    rowVirtualizer,
    nodes,
    activeStickyIndexRef,
    stickyElsRef,
    stickyNodeIndices,
    startByIndex,
    stickyAncestorHeightByIndex,
    maxStickyHeight,
    offsetTop,
    followerMap,
  ]);

  return lastStuckIndex;
}
